/**
 * ScrollTrigger Landing Page Client Component
 *
 * This is an alternative implementation of LandingPage that uses
 * GSAP Observer for vertical section navigation instead of Swiper.js.
 *
 * GSAP Observer provides:
 * - Better trackpad momentum handling (filters out inertia scroll events)
 * - Unified touch/pointer handling across iOS, Android, and hybrid devices
 * - Built-in velocity detection for more accurate scroll direction
 * - Battle-tested cross-browser compatibility
 *
 * IMPORTANT: This is a Client Component ('use client') because it uses:
 * - useState, useEffect, useContext (React hooks)
 * - GSAP Observer (browser APIs)
 * - Event handlers (onClick)
 */

"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { useContext, useRef } from "react"
import { ColoredBackgroundCircle } from "../common/ColoredBackgroundCircle"
import { GlobalContext } from "../common/GlobalContext"
import GlobalContextProvider from "../common/GlobalContextProvider"
import NextSectionButton from "../common/icons/NextSectionButton"
import { Desktop, useIsDesktop } from "../common/MediaQueries"
import { Client } from "../Components/Sections/clients"
import { Employee } from "../Components/Sections/employees"
import HeroHomepage from "../Components/Sections/heroContent/HeroHomepage"
import SectionClients from "../Components/Sections/SectionClients"
import SectionContact from "../Components/Sections/SectionContact"
import SectionHero from "../Components/Sections/SectionHero"
import SectionOurEmployees from "../Components/Sections/SectionOurEmployees"
import SectionProjects from "../Components/Sections/SectionProjects"
import { ProgressBarColors, SectionType } from "../Components/Sections/sections"
import SectionServices from "../Components/Sections/SectionServices"
import {
  SectionChangeInfo,
  useFullPageScrollGsap,
} from "../hooks/useFullPageScrollGsap"

const Styled = {
  Main: styled.main`
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    overflow: hidden;
    z-index: 1;
  `,
  Container: styled.div`
    width: 100%;
    height: 100%;
    position: relative;
  `,
  NextSectionButton: styled(NextSectionButton)`
    width: 43px;
    height: 43px;
    position: fixed;
    bottom: 15px;
    left: calc(50% - 21.5px);
    z-index: 3;
    ${(p) => p.theme.breakpoints.up("md")} {
      bottom: 20px;
      width: 60px;
      height: 60px;
      left: calc(50% - 30px);

      @media (max-height: 650px) {
        width: 50px;
        height: 50px;
        bottom: 40px;
      }
    }
  `,
  ProgressBarContainer: styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: fixed;
    left: 96.5px;
    bottom: 52.5px;
    height: 300px;
    z-index: 3;

    ${(p) => p.theme.breakpoints.up("md")} {
      @media (max-height: 650px) and (max-width: 1100px) {
        left: 65px;
      }

      @media (max-width: 1150px) {
        left: 65px;
      }
    }
  `,
  ProgressBarHeading: styled(Typography)<{ colors: ProgressBarColors }>`
    font-size: 16px;
    color: ${(p) => p.colors.activeColor};
    transform: rotate(-90deg);
    transform-origin: left;
  `,
  ProgressBarStep: styled.div<{
    colors: ProgressBarColors
    activeStep: boolean
  }>`
    width: 2px;
    height: 22px;
    background-color: ${(p) =>
      p.activeStep ? p.colors.activeColor : p.colors.inactiveColor};
  `,
}

interface ScrollTriggerLandingPageProps {
  sections: SectionType[]
  clients: Client[]
  employees: Employee[]
}

function ScrollTriggerLandingPageContent({
  sections,
  clients,
  employees,
}: ScrollTriggerLandingPageProps) {
  const { setActiveSectionIndex, activeSection, setPrev, setNext, setActive } =
    useContext(GlobalContext)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDesktop = useIsDesktop()
  const numberOfSections = Array.from(Array(sections.length).keys())

  // Build section hashes from section data
  const sectionHashes = sections.map((section) => section.hash)

  // Handle section changes from the hook
  // Directly set all states immediately to avoid useEffect delay
  // This eliminates the ~85ms delay caused by React render cycles
  const handleSectionChange = (info: SectionChangeInfo) => {
    if (info.prev !== null) {
      setPrev(info.prev)
    }
    // Set next and active immediately - this bypasses the useEffect delay
    // in GlobalContextProvider and triggers transitions immediately
    setNext(info.current)
    setActive(info.current)
    // Still set activeSectionIndex for other consumers that depend on it
    setActiveSectionIndex(info.current)
  }

  const { currentSection, nextSection } = useFullPageScrollGsap({
    totalSections: sections.length,
    initialSection: 0,
    containerRef,
    sectionHashes,
    onSectionChange: handleSectionChange,
    // Tune these thresholds for better sensitivity control
    wheelThreshold: 50, // Lower = more sensitive to wheel
    touchThreshold: 50, // Minimum touch drag distance in pixels
  })

  return (
    <Styled.Container ref={containerRef}>
      <Styled.Main>
        {activeSection?.background && isDesktop ? (
          <ColoredBackgroundCircle {...activeSection.background[1]} />
        ) : activeSection?.background ? (
          <ColoredBackgroundCircle {...activeSection.background[0]} />
        ) : null}

        <SectionHero position={0}>
          <HeroHomepage section={sections[0] as any} />
        </SectionHero>
        <>
          <SectionServices position={1} section={sections[1] as any} />
          <SectionClients
            position={2}
            clients={clients}
            section={sections[2] as any}
          />
          <SectionProjects position={3} />
          <SectionOurEmployees
            position={sections.length - 2}
            employees={employees}
            section={sections[sections.length - 2] as any}
          />
          <SectionContact
            position={sections.length - 1}
            section={sections[sections.length - 1] as any}
          />
        </>
        {activeSection?.progressBarColors && (
          <Desktop>
            <Styled.ProgressBarContainer>
              <Styled.ProgressBarHeading
                colors={activeSection.progressBarColors}
              >
                {activeSection.progressBarText}
              </Styled.ProgressBarHeading>

              {numberOfSections.map((index) => (
                <Styled.ProgressBarStep
                  key={index}
                  activeStep={index <= currentSection}
                  colors={activeSection.progressBarColors}
                />
              ))}
            </Styled.ProgressBarContainer>
          </Desktop>
        )}
        {currentSection !== sections.length - 1 && (
          <Styled.NextSectionButton
            tabIndex={0}
            role="button"
            onClick={() => nextSection()}
          />
        )}
      </Styled.Main>
    </Styled.Container>
  )
}

import Header from "../layout/Header"

export default function ScrollTriggerLandingPage(
  props: ScrollTriggerLandingPageProps
) {
  return (
    <GlobalContextProvider sections={props.sections}>
      <Header />
      <ScrollTriggerLandingPageContent {...props} />
    </GlobalContextProvider>
  )
}
